import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { adminUsersState } from '@/state/project';

type Props = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
};

const MAX_SUGGESTIONS = 10;

export default function UserIdentifierCombobox({ value, onChange, placeholder = 'User identifier' }: Props) {
  const users = useRecoilValue(adminUsersState);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmedQuery = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!trimmedQuery) return [];
    return users
      .filter((u) => u.identifier.toLowerCase().includes(trimmedQuery))
      .slice(0, MAX_SUGGESTIONS);
  }, [users, trimmedQuery]);

  const showPopover = open && trimmedQuery.length > 0 && users.length > 0;

  return (
    <Popover open={showPopover} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onBlur={(e) => {
            const next = e.relatedTarget as HTMLElement | null;
            if (next && next.closest('[data-radix-popper-content-wrapper]')) {
              return;
            }
            setOpen(false);
          }}
        />
      </PopoverPrimitive.Anchor>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandList className="max-h-56">
            <CommandEmpty>No matching users</CommandEmpty>
            {matches.length > 0 && (
              <CommandGroup>
                {matches.map((u) => (
                  <CommandItem
                    key={u.id}
                    value={u.identifier}
                    onSelect={() => {
                      onChange(u.identifier);
                      setOpen(false);
                      inputRef.current?.focus();
                    }}
                  >
                    <span className="font-medium">{u.identifier}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {u.role}
                      {u.disabled ? ' · disabled' : ''}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
